import { useForm } from '@tanstack/react-form'
import { FilterIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { InventoryFilter } from '@/schemas/filterSchemas'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Badge } from '@/components/custom/badge'
import IconInput from '@/components/custom/icon-input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useIsMobile } from '@/hooks'

const inventoryCategories = [
  'Electronics',
  'Furniture',
  'Supplies',
  'Appliances',
  'Tools',
  'Other',
]

export default function InventoryFilter({
  onFilter,
  onCloseDrawer,
  initialFilters,
}: {
  onFilter?: (filters: InventoryFilter) => void
  onCloseDrawer?: () => void
  initialFilters: InventoryFilter
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  const form = useForm({
    defaultValues: {
      search: initialFilters.search,
      category: initialFilters.category,
      inStock: initialFilters.inStock,
    },
    onSubmit: ({ value }) => {
      onFilter?.({
        ...value,
        category: value.category,
        inStock: !!value.inStock,
      })
      setOpen(false)
      if (isMobile && onCloseDrawer) onCloseDrawer()
    },
  })

  useEffect(() => {
    form.setFieldValue('category', initialFilters.category)
    form.setFieldValue('inStock', initialFilters.inStock)
  }, [initialFilters])

  const handleClear = () => {
    form.reset()

    onFilter?.({
      category: [],
      inStock: false,
      search: isMobile ? '' : initialFilters.search,
    })
    setOpen(false)
  }

  const filtersActive =
    form.state.values.category.length > 0 || form.state.values.inStock
  const activeCount = [
    form.state.values.category.length,
    form.state.values.inStock ? 1 : 0,
  ].filter(Boolean).length

  const filterForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {isMobile && (
        <form.Field
          name="search"
          children={(field) => (
            <IconInput
              placeholder="Search by item name, description"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              className="w-full"
            />
          )}
        />
      )}
      <form.Field
        name="category"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Categories"
            id={field.name}
            name={field.name}
            options={inventoryCategories.map((cat) => ({
              value: cat,
              label: cat,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="inStock"
        children={(field) => {
          return (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={field.state.value}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-primary h-4 w-4"
              />
              <label
                htmlFor={field.name}
                className="text-sm select-none cursor-pointer"
              >
                Show only items in stock
              </label>
            </div>
          )
        }}
      />
      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.values.category,
          state.values.inStock,
          state.values.search,
        ]}
        children={([
          canSubmit,
          churchIdValue,
          categoryValue,
          inStockValue,
          searchValue,
        ]) => {
          const hasFilter =
            (Array.isArray(churchIdValue) && churchIdValue.length) ||
            (Array.isArray(categoryValue) && categoryValue.length) ||
            inStockValue
          const hasMobileSearch = isMobile && searchValue
          return (
            <div
              className={
                !isMobile ? 'flex gap-2 justify-end pt-2' : 'space-y-2'
              }
            >
              <Button type="button" variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || (!hasFilter && !hasMobileSearch)}
              >
                Apply
              </Button>
            </div>
          )
        }}
      />
    </form>
  )

  if (isMobile) {
    return <div className="w-full">{filterForm}</div>
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={filtersActive ? 'relative' : ''}>
          <FilterIcon size={16} />
          Filters
          {filtersActive && <Badge color="red">{activeCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-4 min-w-[320px] max-w-100">
        {filterForm}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
